import { type FunctionalComponent, h } from '@stencil/core'
import type { DebugConfig, DebugSettings, ToolbarPosition } from '../utils/storage'

export interface SettingsPanelProps {
  onResetAll: () => void
  keepOpenOnClickOutside: boolean
  position: ToolbarPosition
  debugSettings: DebugSettings
  onKeepOpenChange: (value: boolean) => void
  onPositionChange: (value: ToolbarPosition) => void
  onPublishedJsChange: (value: boolean) => void
  localStorageSize: number
  debugConfig: DebugConfig | null
}

export const SettingsPanel: FunctionalComponent<SettingsPanelProps> = ({
  onResetAll,
  keepOpenOnClickOutside,
  position,
  debugSettings,
  onKeepOpenChange,
  onPositionChange,
  onPublishedJsChange,
  localStorageSize,
  debugConfig
}) => (
  <div class="debug-toolbar-panel open">
    <div class="debug-toolbar-header">
      <h3>Settings</h3>
    </div>
    <div class="debug-toolbar-section">
      <h4>Environment</h4>
      {debugConfig ? (
        <div>
          <rtr-env-select
            mode="environment"
            environmentOptions={debugConfig.environments}
            stagingOptions={debugConfig.stagingOptions}
            baseUrls={debugConfig.baseUrls}
            urlPattern={debugConfig.urlPattern}
          />
          <rtr-env-select
            mode="staging"
            environmentOptions={debugConfig.environments}
            stagingOptions={debugConfig.stagingOptions}
            baseUrls={debugConfig.baseUrls}
            urlPattern={debugConfig.urlPattern}
          />
        </div>
      ) : (
        <div
          class="debug-toolbar-info"
          style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}
        >
          Configuration unavailable. Environment settings cannot be changed.
        </div>
      )}

      <div class="debug-toolbar-control">
        <label class="debug-toolbar-checkbox">
          <input
            type="checkbox"
            checked={debugSettings.publishedJs}
            onChange={e => onPublishedJsChange((e.target as HTMLInputElement).checked)}
            disabled={true}
          />
          <span>
            Use prod JS (for testing purposes) <span style={{ fontSize: '8px', color: 'gray' }}>(feature yet to be implemented)</span>
          </span>
        </label>
      </div>
    </div>
    <div class="debug-toolbar-section">
      <h4>Behavior</h4>
      <div class="debug-toolbar-control">
        <label class="debug-toolbar-checkbox">
          <input
            type="checkbox"
            checked={keepOpenOnClickOutside}
            onChange={e => onKeepOpenChange((e.target as HTMLInputElement).checked)}
          />
          <span>Keep toolbar open on click outside</span>
        </label>
      </div>
    </div>
    <div class="debug-toolbar-section">
      <h4>Position</h4>
      <div class="debug-toolbar-control">
        <label htmlFor="debugToolbarPosition">Toolbar Position</label>
        <select
          id="debugToolbarPosition"
          onChange={e => onPositionChange((e.target as HTMLSelectElement).value as ToolbarPosition)}
        >
          <option
            value="top-left"
            selected={position === 'top-left'}
          >
            Top Left
          </option>
          <option
            value="top-center"
            selected={position === 'top-center'}
          >
            Top Center
          </option>
          <option
            value="top-right"
            selected={position === 'top-right'}
          >
            Top Right
          </option>
          <option
            value="bottom-left"
            selected={position === 'bottom-left'}
          >
            Bottom Left
          </option>
          <option
            value="bottom-center"
            selected={position === 'bottom-center'}
          >
            Bottom Center
          </option>
          <option
            value="bottom-right"
            selected={position === 'bottom-right'}
          >
            Bottom Right
          </option>
        </select>
      </div>
    </div>
    <div class="debug-toolbar-section">
      <div class="debug-toolbar-control">
        <button
          type="button"
          class="debug-toolbar-button"
          onClick={onResetAll}
          style={{ width: '100%' }}
        >
          Reset All Settings
        </button>
      </div>
      <div
        class="debug-toolbar-info"
        style={{ marginTop: '12px' }}
      >
        <strong>Keyboard Shortcuts:</strong>
        <br />
        <code>Ctrl+Shift+D</code> - Toggle toolbar visibility
      </div>
      <div
        class="debug-toolbar-info"
        style={{ marginTop: '12px' }}
      >
        <strong>Local Storage:</strong>
        <br />
        Size: <code>{localStorageSize} KB</code>
      </div>
    </div>
  </div>
)
